/// <amd-module name="VSS/Features/Markdown/MarkdownRenderer" />

import * as MarkdownIt from "markdown-it";
import * as MarkdownItContainer from "markdown-it-container";
import * as MarkdownItEmoji from "markdown-it-emoji";
import * as MarkdownItEmojiOld from "markdown-it-emoji-old";
import * as MarkdownItImSize from "markdown-it-imsize";
import * as MarkdownItTaskLists from "markdown-it-task-lists";
import { filterXSS, whiteList, escapeAttrValue, IFilterXSSOptions, friendlyAttrValue, safeAttrValue } from "xss";

import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

import { removeMarkdown } from "VSS/Features/Markdown/3rdParty/Utils/RemoveMarkdown";
import { MarkdownConstants } from "VSS/Features/Markdown/MarkdownConstants";
import { ContainerOptions, MarkdownRendererOptions } from "VSS/Features/Markdown/MarkdownRendererContracts";
import { AnchoredHeaders } from "VSS/Features/Markdown/Plugins/AnchoredHeaders";
import { ImageTransformer } from "VSS/Features/Markdown/Plugins/ImageTransformer";
import { LazyHeaders } from "VSS/Features/Markdown/Plugins/LazyHeaders";
import { LinkTransformer } from "VSS/Features/Markdown/Plugins/LinkTransformer";
import { MarkdownLinesMapping } from "VSS/Features/Markdown/Plugins/MarkdownLinesMapping";
import { MarkdownMetadata } from "VSS/Features/Markdown/Plugins/MarkdownMetadata";
import { TableOfContents } from "VSS/Features/Markdown/Plugins/TableOfContents";
import { TableOfContentsPreProcessor } from "VSS/Features/Markdown/Plugins/TableOfContentsPreProcessor";
import * as Resources from "VSS/Features/Markdown/Resources";
import { HtmlNormalizer } from "VSS/Features/Markdown/Utils/HtmlNormalizer";
import { getEncodedHeaderText } from "VSS/Features/Markdown/Utils/MarkdownItUtils";
import { IMarkdownItKatexService } from "VSS/Features/MarkdownItKatex/IMarkdownItKatexService";
import { TableOfSubPagesPlugin } from "VSS/Features/Markdown/Plugins/TableOfSubPagesPlugin";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

const KatexServiceContributionId = "ms.vss-features.markdown-it-katex-service";

const xssOptions: IFilterXSSOptions = {
    whiteList,
    onIgnoreTag: function (tag: string, html: string, options) {
      return html;
    },
    onIgnoreTagAttr: function (tag: string, name: string, value: string, isWhiteAttr: boolean) {
      return name + '="' + escapeAttrValue(value) + '"';
    },
    safeAttrValue: function(tag: string, name: string, value: string, cssFilter: XSS.ICSSFilter) {
        // Relative links are transformed before it hits this block of code, this lets them pass through.
        if (tag === 'a' && name === 'href' && value.charAt(0) === '?') {
            return value;
        } else if (tag === 'img' && name === 'src' && value.slice(0, 5) === "blob:") {
            value = friendlyAttrValue(value);
            return escapeAttrValue(value);
        } else {
            return safeAttrValue(tag, name, value, cssFilter);
        }
    }
}

export class MarkdownRenderer {
    private _options: MarkdownRendererOptions;
    private _renderer: MarkdownIt.MarkdownIt;

    constructor(
        private _pageContext: IVssPageContext,
        options?: MarkdownRendererOptions,
        getMarkdownRenderer?: (options?: MarkdownRendererOptions) => MarkdownIt.MarkdownIt // For UTs only
    ) {
        this._options = options || {};
        this._renderer = this._getRenderer(getMarkdownRenderer);
    }

    public parse = (content: string): MarkdownIt.Token[] => {
        return this._renderer.parse(content, {});
    };

    public renderHtml = (content: string): string => {
        let renderedContent = this._renderer.render(content);

        if (this._options.html) {
            // Katex needs top value to be set
            const additionalValidStyles = this._options.enableKatex ? ["top"] : undefined;
            const additionalInvalidProtocols = ["DATA"];
            const additionalValidAttributes = [MarkdownConstants.MarkDownLinesMappingAttrName];

            renderedContent = filterXSS(renderedContent, xssOptions);

            renderedContent = HtmlNormalizer.normalizeStripAttributes(
                renderedContent,
                undefined,
                additionalValidAttributes,
                undefined,
                additionalValidStyles,
                true,
                additionalInvalidProtocols
            );
        }

        return renderedContent;
    };

    private _getRenderer(getMarkdownRenderer?: (options?: MarkdownRendererOptions) => MarkdownIt.MarkdownIt): MarkdownIt.MarkdownIt {
        const options = this._options;
        const {
            clickableTaskList,
            containerOptions,
            disableToc,
            enableEmoji,
            enableHeaderAnchorSharing,
            enableImageSize,
            enableKatex,
            enableMarkdownLinesMapping,
            enableYaml,
            linkifyTlds,
            onKatexLoad,
            onKatexLoadFailed,
            onKatexLoadStarted,
            onMarkdownLinesMappingComplete,
            validateLink
        } = options;

        const renderer = getMarkdownRenderer ? getMarkdownRenderer(options) : MarkdownIt(options);

        renderer.use((md: MarkdownIt.MarkdownIt) => new LazyHeaders(md));
        renderer.use((md: MarkdownIt.MarkdownIt) => new AnchoredHeaders(md, enableHeaderAnchorSharing));
        renderer.use((md: MarkdownIt.MarkdownIt) => new LinkTransformer(md, options));
        renderer.use((md: MarkdownIt.MarkdownIt) => new ImageTransformer(md, options));
        renderer.use(MarkdownItTaskLists, { enabled: clickableTaskList });

        if (enableMarkdownLinesMapping) {
            renderer.use((md: MarkdownIt.MarkdownIt) => new MarkdownLinesMapping(md, onMarkdownLinesMappingComplete));
        }

        if (enableEmoji) {
            if (isFeatureFlagEnabled(this._pageContext, "VisualStudio.Services.Markdown.UseNewVersionOfMarkdownItEmoji", false)) {
                renderer.use(MarkdownItEmoji);
            } else {
                renderer.use(MarkdownItEmojiOld);
            }
        }

        if (enableImageSize) {
            renderer.use(MarkdownItImSize);
        }

        if (containerOptions) {
            containerOptions.forEach((containerOption: ContainerOptions) => {
                renderer.use(MarkdownItContainer, containerOption.name, containerOption);
            });
            renderer.renderer.rules[MarkdownConstants.MaskedTokenType] = (tokens, idx, options, env, self) => "";
        }

        if (linkifyTlds) {
            renderer.linkify.tlds(linkifyTlds as any, false);
        }

        if (validateLink) {
            renderer.validateLink = validateLink;
        }

        if (enableKatex) {
            const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
            const markdownItKatexService = contributionService.getServiceSync<IMarkdownItKatexService>(KatexServiceContributionId);
            if (markdownItKatexService) {
                markdownItKatexService.register(renderer);
            } else {
                onKatexLoadStarted && onKatexLoadStarted();

                contributionService.getService<IMarkdownItKatexService>(KatexServiceContributionId).then(
                    (markdownItKatexService: IMarkdownItKatexService | undefined) => {
                        if (markdownItKatexService) {
                            markdownItKatexService.register(renderer);
                            if (onKatexLoad) {
                                onKatexLoad();
                            }
                        } else {
                            onKatexLoadFailed && onKatexLoadFailed();
                        }
                    },
                    () => {
                        onKatexLoadFailed && onKatexLoadFailed();
                    }
                );
            }
        }

        if (options.subPages) {
            renderer.use((md: MarkdownIt.MarkdownIt) => new TableOfSubPagesPlugin(md, {
                subPages: options.subPages,
                containerClass: "tosp-container",
                containerHeaderHtml: '<div class="tosp-container-header">' + Resources.MarkdownTospHeading + "</div>"
            }));
        }

        if (!disableToc) {
            renderer.use(
                (md: MarkdownIt.MarkdownIt) =>
                    new TableOfContentsPreProcessor(md, MarkdownConstants.TOCDefaultMarker, MarkdownConstants.NoTOCDefaultMarker)
            );
            renderer.use(
                (md: MarkdownIt.MarkdownIt) =>
                    new TableOfContents(md, {
                        includeLevel: [1, 2, 3, 4, 5, 6],
                        containerClass: "toc-container",
                        markerPattern: MarkdownConstants.TOCDefaultMarkerPattern,
                        forceFullToc: true,
                        slugify: (headingContent: string) => {
                            return getEncodedHeaderText(renderer, headingContent);
                        },
                        format: (headingContent: string) => {
                            headingContent = removeMarkdown(headingContent, { stripListLeaders: false });
                            return this._renderer.utils.unescapeMd(headingContent);
                        },
                        containerHeaderHtml: '<div class="toc-container-header">' + Resources.MarkdownTocHeading + "</div>"
                    })
            );
        }

        if (enableYaml) {
            renderer.use((md: MarkdownIt.MarkdownIt) => new MarkdownMetadata(md));
        }

        return renderer;
    }
}
